import { Router } from '@angular/router';
import {
  HostError,
  WidgetHandoffRecord,
  WidgetHostHandlers,
  handoffGrant,
} from '@finxone-platform/shared/app-sdk';
import { Store } from '@ngxs/store';
import { Observable, firstValueFrom, lastValueFrom, take } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { SavingsAccountService } from '../../../../lib/services/savings-account-service/savings-account.service';
import {
  WidgetHandoffTransport,
  selectWidgetHandoffTransport,
} from '../../../../lib/services/widget-handoff/widget-handoff-transport';
import {
  WIDGET_HANDOFF_ID_PARAM,
  WIDGET_HANDOFF_RETURN_PATH,
  WidgetHandoffService,
} from '../../../../lib/services/widget-handoff/widget-handoff.service';
import {
  WidgetHandoffEntry,
  WidgetInstanceScope,
} from '../../../../lib/services/widget-handoff/widget-handoff.type';
import { SavingsAccountState } from '../../../../lib/state/savings-account.state';

/**
 * Hand-off kinds this host supports. A kind is only startable when the
 * instance's api grants contain `handoff:<kind>` (handoffGrant) — the host core
 * enforces that before these run, so nothing here re-checks capabilities.
 */
export const CUSTOM_WIDGET_HANDOFF_KINDS = ['deposits.pisp'] as const;

/** The api grant entries a widget needs to start each supported kind. */
export const CUSTOM_WIDGET_HANDOFF_GRANTS = CUSTOM_WIDGET_HANDOFF_KINDS.map(handoffGrant);

/**
 * Projection onto the fx-widget contract: the widget gets back what it declared
 * plus what happened, never the host-only correlation (`returnUrl`, the
 * provider request ids) and never the owning instance ids.
 */
export function toWidgetHandoffRecord(entry: WidgetHandoffEntry): WidgetHandoffRecord {
  const record: WidgetHandoffRecord = {
    handoffId: entry.handoffId,
    kind: entry.kind,
    status: entry.status,
    params: entry.params,
    startedAt: entry.startedAt,
  };
  if (entry.result) record.result = entry.result;
  if (entry.error) record.error = entry.error;
  return record;
}

export interface CustomWidgetHandoffDeps {
  scope: WidgetInstanceScope;
  store: Store;
  savingsAccountService: SavingsAccountService;
  configService: ConfigService;
  router: Router;
  handoffService: WidgetHandoffService;
  /**
   * Commits the navigation off-origin. Defaults to a location assign because
   * the destination is a bank/vendor URL, not an Angular route; tests and the
   * builder preview substitute it. It is handed to whichever transport is
   * selected, so a shell still gets its bridge signal around it.
   */
  navigateToVendor?: (url: string) => void;
  /**
   * How the off-origin leg runs in this environment. Defaults to runtime
   * detection (selectWidgetHandoffTransport) so the widget never learns whether
   * it is inside a browser or a native shell; tests substitute one.
   */
  transport?: WidgetHandoffTransport;
}

/**
 * The 'handoffs' data topic plus the handoff/state handler groups, all scoped
 * to one widget instance.
 *
 * Every read goes through WidgetHandoffService with this instance's scope, so a
 * widget can only ever see its own hand-offs and its own scratchpad — the
 * 'handoffs' grant says "may read hand-offs", never "may read all hand-offs".
 */
export function buildCustomWidgetHandoffBridge(deps: CustomWidgetHandoffDeps): {
  handoff: NonNullable<WidgetHostHandlers['handoff']>;
  state: NonNullable<WidgetHostHandlers['state']>;
  topic: {
    get(): Promise<WidgetHandoffRecord[]>;
    subscribe(push: (payload: WidgetHandoffRecord[]) => void): () => void;
  };
} {
  const { scope, store, savingsAccountService, configService, router, handoffService } = deps;
  /**
   * Resolved per hand-off rather than once per bridge, so a shell bridge that
   * appears after the widget mounted is still detected. Selection itself is the
   * only place the environment is inspected (widget-handoff-transport.ts).
   */
  const resolveTransport = (): WidgetHandoffTransport =>
    deps.transport ?? selectWidgetHandoffTransport({ configService, navigate: deps.navigateToVendor });

  /**
   * PISP deposit into a savings account. Native parity with
   * initiatePispDeposit (pisp-deposit.utils.ts) except for the two things a
   * sandboxed widget cannot participate in:
   *
   * - The GoCardless drop-in component is an in-page modal driven by a global
   *   script and the platform's own QR/deep-link zone pages. A widget hand-off
   *   always takes the hosted billing-request-flow path (isCustomPaymentPage
   *   omitted) and hands the authorisation URL the backend returns to the
   *   selected transport.
   * - The redirectUri is this host's return route rather than
   *   /pisp-deposit-status, because the outcome has to be written onto the
   *   hand-off record before the widget's page is restored.
   */
  async function startPispDeposit(params: Record<string, unknown>): Promise<{ handoffId: string }> {
    const amount = Number(params['amount']);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new HostError('host-error', 'deposits.pisp requires a positive numeric amount');
    }
    const savingsAccountId =
      (params['savingsAccountId'] ? String(params['savingsAccountId']) : '') ||
      store.selectSnapshot(SavingsAccountState.getCurrentAccountId);
    if (!savingsAccountId) {
      throw new HostError(
        'host-error',
        'deposits.pisp requires savingsAccountId (no savings account selected)',
      );
    }
    const configValues = await firstValueFrom(configService.getConfig().pipe(take(1)));

    // Persisted before the provider call: once the browser leaves the origin
    // there is nothing left in memory to correlate the return against.
    const entry = handoffService.start(scope, {
      kind: 'deposits.pisp',
      params: { ...params, amount, savingsAccountId },
      returnUrl: router.url,
    });
    const returnUri =
      `${configValues.api_endpoints.default}${WIDGET_HANDOFF_RETURN_PATH}` +
      `?${WIDGET_HANDOFF_ID_PARAM}=${encodeURIComponent(entry.handoffId)}`;

    try {
      const response = await lastValueFrom(
        savingsAccountService
          .initiatePispDeposit(savingsAccountId, {
            amount,
            redirectUri: returnUri,
            exitUri: returnUri,
          })
          .pipe(take(1)),
      );
      // `id` is the billing request the status check is keyed by; the
      // authorisation URL is the customer's bank.
      handoffService.setCorrelation(entry.handoffId, {
        pispDepositRequestId: response?.id,
        savingsAccountId,
      });
      if (!response?.authorisationUrl) {
        throw new Error('deposits.pisp: provider returned no authorisation url');
      }
      await resolveTransport().startHandoff(response.authorisationUrl);
      // Resolves as "begun", not as "about to be torn down": whether this
      // document survives is the transport's business and the widget's
      // contract deliberately does not say (see WidgetHandoffApi).
      return { handoffId: entry.handoffId };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      handoffService.fail(entry.handoffId, message);
      throw error instanceof HostError ? error : new HostError('host-error', message);
    }
  }

  return {
    handoff: {
      start: async (kind, params) => {
        if (kind === 'deposits.pisp') return startPispDeposit(params);
        throw new HostError('unknown-operation', `unknown-handoff-kind: ${kind}`);
      },
    },
    state: {
      get: async () => handoffService.getState(scope),
      set: async (value) => handoffService.setState(scope, value),
    },
    topic: {
      get: async () => handoffService.list(scope).map(toWidgetHandoffRecord),
      /**
       * The in-session push. records$ re-emits on every store mutation that
       * changes this instance's records, so a widget that survived its hand-off
       * (a transport that never navigated, or a hand-off that failed before
       * control left) sees the terminal record without a remount. Nothing has to
       * be wired in custom-widget-data-topics.ts: withHandoffsTopic below routes
       * 'handoffs' here, and the app-sdk host core pushes it over the port
       * like any other data topic.
       */
      subscribe: (push) => {
        const subscription = (handoffService.records$(scope) as Observable<WidgetHandoffEntry[]>).subscribe(
          (entries) => push(entries.map(toWidgetHandoffRecord)),
        );
        return () => subscription.unsubscribe();
      },
    },
  };
}

/**
 * Serves 'handoffs' from the hand-off bridge and everything else from the Ngxs
 * topics, so the widget sees one data handler group.
 */
export function withHandoffsTopic(
  topics: NonNullable<WidgetHostHandlers['data']>,
  handoffs: ReturnType<typeof buildCustomWidgetHandoffBridge>['topic'],
): NonNullable<WidgetHostHandlers['data']> {
  return {
    get: (topic) => (topic === 'handoffs' ? handoffs.get() : topics.get(topic)),
    subscribe: (topic, push) =>
      topic === 'handoffs' ? handoffs.subscribe(push) : (topics.subscribe?.(topic, push) ?? (() => undefined)),
  };
}
